import { Alert, Card, Collapse, ConfigProvider, Empty, Flex, Space, Tag, Typography, theme } from 'antd';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faList } from '@fortawesome/free-solid-svg-icons';
import { CONNECTION_TYPES } from '@shared/constants/connectionTypes';
import {
  buildCallbackSummary,
  describeHttpLogEntry,
  formatCallbackTimestamp,
  MONO_FONT,
} from './utils';

const { Text } = Typography;

const EVENT_TAG_COLORS = {
  TASK_STARTED: 'cyan',
  STEP_UPDATE: 'blue',
  TASK_COMPLETED: 'green',
  TASK_FAILED: 'red',
  HEALTH_REPORT: 'gold',
};

const TIME_COLUMN_STYLE = {
  width: 148,
  flexShrink: 0,
  fontVariantNumeric: 'tabular-nums',
  whiteSpace: 'nowrap',
};

const expandedPreStyle = {
  margin: 0,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
  fontSize: 11,
  lineHeight: 1.45,
};

const CollapseItemLabel = ({ label, labelStyle, tag, tagColor = 'default', summary }) => (
  <Flex
    gap={8}
    align='center'
    style={{
      fontFamily: MONO_FONT,
      fontSize: '0.75em',
    }}
  >
    <Text
      style={{
        color: 'inherit',
        fontFamily: 'inherit',
        fontSize: 'inherit',
        ...(labelStyle || { whiteSpace: 'nowrap' }),
      }}
    >
      {label}
    </Text>
    {tag && (
      <Tag color={tagColor} style={{ marginInlineEnd: 0, borderRadius: 4 }}>
        {tag}
      </Tag>
    )}
    {summary && (
      <Text
        ellipsis={{
          tooltip: {
            mouseEnterDelay: 0.5,
            styles: {
              root: { maxWidth: '90vw' },
            },
          },
        }}
        style={{
          color: 'inherit',
          width: '20em',
          flex: 1,
          fontFamily: 'inherit',
          fontSize: 'inherit',
        }}
      >
        {summary}
      </Text>
    )}
  </Flex>
);

const buildLogItem = ({ key, label, labelStyle, tag, tagColor, summary, content }) => ({
  key,
  label: (
    <CollapseItemLabel
      label={label}
      labelStyle={labelStyle}
      tag={tag}
      tagColor={tagColor}
      summary={summary}
    />
  ),
  children: <pre style={expandedPreStyle}>{content}</pre>,
});

const buildCallbackItems = (callbacks) => (
  callbacks.map((callback, index) => {
    const eventType = callback?.event_type || 'UNKNOWN';
    const callbackKey = [
      index,
      eventType,
      callback.payload?.correlation_id || 'no-correlation',
      callback.payload?.metadata?.timestamp || callback.payload?.step || 'no-timestamp',
    ].join('-');

    return buildLogItem({
      key: callbackKey,
      label: formatCallbackTimestamp(callback),
      labelStyle: TIME_COLUMN_STYLE,
      tag: eventType,
      tagColor: EVENT_TAG_COLORS[eventType] || 'default',
      summary: buildCallbackSummary(callback),
      content: JSON.stringify(callback, null, 2),
    });
  })
);

const buildHttpItems = (rootEntries) => (
  rootEntries.map(([entryKey, value]) => {
    const { tag, tagColor, summary } = describeHttpLogEntry(value);

    return buildLogItem({
      key: entryKey,
      label: entryKey,
      tag,
      tagColor,
      summary,
      content: JSON.stringify(value, null, 2),
    });
  })
);

const LogConsoleBody = ({ logError, isRpa, callbacks, rootEntries }) => {
  const { token } = theme.useToken();
  const panelStyle = {
    marginBottom: 8,
    background: token.colorFillAlter,
    borderRadius: token.borderRadiusLG,
    border: 'none',
  };

  if (logError) {
    return (
      <div style={{ padding: 16 }}>
        <Alert
          type='warning'
          showIcon
          message='No fue posible recuperar el log'
          description={logError}
        />
      </div>
    );
  }

  const items = (isRpa ? buildCallbackItems(callbacks) : buildHttpItems(rootEntries))
    .map((item, index, allItems) => ({
      ...item,
      style: index === allItems.length - 1
        ? { ...panelStyle, marginBottom: 0 }
        : panelStyle,
    }));

  if (items.length === 0) {
    return (
      <Flex align='center' justify='center' style={{ minHeight: 200, flex: 1 }}>
        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} />
      </Flex>
    );
  }

  return (
    <Collapse
      size='small'
      items={items}
      style={{
        background: 'transparent',
        border: 'none',
      }}
    />
  );
};

const LogConsoleCard = ({ logError, isRpa, callbacks, rootEntries }) => (
  <Card
    style={{
      minHeight: '30vh',
      maxHeight: '40vh',
      overflow: 'auto',
      display: 'flex',
    }}
    styles={{
      body: {
        padding: 0,
        display: 'flex',
        flexDirection: 'column',
        flex: 1,
      },
    }}
  >
    <LogConsoleBody
      logError={logError}
      isRpa={isRpa}
      callbacks={callbacks}
      rootEntries={rootEntries}
    />
  </Card>
);

export const ExecutionLog = ({ data }) => {
  const connectionType = data?.connectionType;
  const isRpa = connectionType === CONNECTION_TYPES.RPA;
  const log = data?.log;
  const logError = data?.errors?.log;

  const callbacks = Array.isArray(log?.callbacks) ? log.callbacks : [];
  const rootEntries = (!log || typeof log !== 'object' || Array.isArray(log))
    ? []
    : Object.entries(log);

  const entriesCount = isRpa ? callbacks.length : rootEntries.length;
  const entriesLabel = isRpa
    ? `${entriesCount} evento${entriesCount === 1 ? '' : 's'}`
    : `${entriesCount} entrada${entriesCount === 1 ? '' : 's'}`;

  return (
    <Flex vertical gap='small'>
      <Flex justify='space-between' align='baseline'>
        <Space>
          <FontAwesomeIcon icon={faList} />
          <Text strong>{isRpa ? 'Historial de eventos' : 'Log'}</Text>
        </Space>
        {!logError && (
          <Text type='secondary' style={{ fontSize: '0.8em' }}>
            {entriesLabel}
          </Text>
        )}
      </Flex>
      <ConfigProvider theme={{ algorithm: theme.darkAlgorithm }}>
        <LogConsoleCard
          logError={logError}
          isRpa={isRpa}
          callbacks={callbacks}
          rootEntries={rootEntries}
        />
      </ConfigProvider>
    </Flex>
  );
};
